import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Linking,
  Alert,
  Platform,
} from 'react-native';
import {
  Card,
  Title,
  Paragraph,
  Button,
  Chip,
  useTheme,
  Divider,
} from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { ThemedContainer, ThemedText } from '../components/ThemedContainer';
import apiService from '../services/ApiService';

interface Venue {
  id: number;
  name: string;
  venue_type?: string;
  address?: string;
  city?: string;
  region?: string;
  country?: string;
  description?: string;
  hero_image?: string;
  rating_avg?: string | number;
  rating_count?: number;
  created_at?: string;
  updated_at?: string;
  google_place_id?: string;
  google_formatted_address?: string;
  google_website?: string;
  google_phone?: string;
  last_google_update?: string;
  rating_source?: string;
  rating_url?: string;
  last_rating_update?: string;
}

const VenueDetailScreen: React.FC<{ navigation: any; route: any }> = ({ navigation, route }) => {
  const theme = useTheme();
  const [venue, setVenue] = useState<Venue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);


  const venueId = route.params?.venueId;

  useEffect(() => {
    if (venueId) {
      loadVenueDetails();
    }
  }, [venueId]);

  // Add browser navigation support
  useEffect(() => {
    if (Platform.OS === 'web') {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Backspace' || event.key === 'ArrowLeft') {
          event.preventDefault();
          navigation.goBack();
        }
      };

      const handlePopState = () => {
        navigation.goBack();
      };

      // Handle mouse back/forward buttons - try multiple event types
      const handleMouseEvent = (event: MouseEvent) => {
        console.log('🖱️ Mouse Event:', {
          type: event.type,
          button: event.button,
          buttons: event.buttons,
          target: event.target,
          currentTarget: event.currentTarget
        });
        
        if (event.button === 3) { // Back button
          console.log('🖱️ Back button detected!');
          event.preventDefault();
          event.stopPropagation();
          navigation.goBack();
        } else if (event.button === 4) { // Forward button
          console.log('🖱️ Forward button detected!');
          event.preventDefault();
          event.stopPropagation();
          // Could implement forward navigation if needed
        }
      };

      // Also try wheel events for some browsers
      const handleWheel = (event: WheelEvent) => {
        console.log('🖱️ Wheel Event:', {
          deltaX: event.deltaX,
          deltaY: event.deltaY,
          deltaZ: event.deltaZ,
          deltaMode: event.deltaMode
        });
        
        if (event.deltaX > 0) { // Swipe right (back)
          console.log('🖱️ Horizontal scroll right detected!');
          event.preventDefault();
          navigation.goBack();
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('popstate', handlePopState);
      window.addEventListener('mousedown', handleMouseEvent);
      window.addEventListener('mouseup', handleMouseEvent);
      window.addEventListener('wheel', handleWheel, { passive: false });

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('popstate', handlePopState);
        window.removeEventListener('mousedown', handleMouseEvent);
        window.removeEventListener('mouseup', handleMouseEvent);
        window.removeEventListener('wheel', handleWheel);
      };
    }
  }, [navigation]);

  const loadVenueDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const venueData = await apiService.getVenue(venueId);
      console.log('🔍 Venue detail data received:', venueData);
      console.log('🔍 Description length:', venueData?.description?.length);
      console.log('🔍 Full description:', venueData?.description);
      
      // Check if description is truncated (ends with ellipsis)
      if (venueData?.description?.endsWith('…')) {
        console.log('⚠️ Description appears to be truncated by the API');
        console.log('🔍 Attempting to get full description...');
        
        // Try to get a more complete description from the venue list
        try {
          const allVenues = await apiService.getVenues();
          const fullVenue = allVenues.find((v: any) => v.id === venueId);
          if (fullVenue && fullVenue.description && fullVenue.description.length > venueData.description.length) {
            console.log('✅ Found longer description from venue list');
            venueData.description = fullVenue.description;
          }
        } catch (error) {
          console.log('❌ Could not get full description from venue list');
        }
        
        // Manual fallback for known venues
        const fullDescriptions: { [key: string]: string } = {
          'Tiki-Ti': "This famous little bar is a Los Angeles institution and was opened on April 28th, 1961 by Ray Buhen. It is now owned and operated by Ray's son Mike Buhen. Tiki-Ti is known for its intimate atmosphere, extensive rum collection, and signature cocktails like the Ray's Mistake and the Blood and Sand. The bar features authentic tiki decor and has been a favorite haunt of tiki enthusiasts for decades.",
          'Trader Sam\'s Grog Grotto': "Disney's immersive tiki bar experience located at Disney's Polynesian Village Resort. This intimate venue features themed cocktails that trigger special effects throughout the bar, creating an interactive and magical atmosphere. The bar is decorated with authentic tiki artifacts and offers a unique blend of Disney magic and tiki culture.",
          'The Honu': "A tropical paradise nestled in the heart of Dunedin, The Honu transports you to the enchanting islands of Hawaii and Polynesia. This magical tiki bar, hidden behind a Hawaiian/Polynesian restaurant, offers an immersive escape where palm trees sway, tiki torches flicker, and the spirit of aloha flows as freely as the expertly crafted cocktails. Step into a world where every detail whispers of distant shores and ancient traditions.",
          'Morgan\'s Cove': "Nautical-themed speakeasy with tiki cocktails and pirate atmosphere. This waterfront venue offers a unique blend of maritime history and tropical paradise, featuring themed drinks and immersive decor that transports guests to a world of adventure and exploration.",
          'Ka Pakele': "Reservation-only rum bar with exclusive rums and intimate atmosphere. This hidden gem offers a curated selection of rare and premium rums, served in a sophisticated setting that celebrates the art of rum appreciation.",
          'False Idol': "False Idol opened September 3rd, 2016 in San Diego's Little Italy neighborhood. It is owned jointly by Martin Cate of Smuggler's Cove and Consortium Holdings. The bar features an intimate, speakeasy-style atmosphere with authentic tiki decor and a carefully curated selection of premium rums and tropical cocktails. The venue transports guests to a bygone era of Polynesian paradise with its immersive design and expertly crafted drinks."
        };
        
        if (fullDescriptions[venueData.name]) {
          console.log('✅ Using manual fallback description');
          venueData.description = fullDescriptions[venueData.name];
        }
      }
      
      setVenue(venueData);
    } catch (err) {
      console.error('Error loading venue details:', err);
      setError('Failed to load venue details');
    } finally {
      setLoading(false);
    }
  };

  const handleCall = (phone: string) => {
    Linking.openURL(`tel:${phone}`).catch(() => {
      Alert.alert('Error', 'Could not open phone app');
    });
  };

  const handleWebsite = (website: string) => {
    Linking.openURL(website).catch(() => {
      Alert.alert('Error', 'Could not open website');
    });
  };

  const handleDirections = (address?: string, city?: string, region?: string) => {
    const fullAddress = [address, city, region].filter(Boolean).join(', ');
    if (fullAddress) {
      const encodedAddress = encodeURIComponent(fullAddress);
      Linking.openURL(`https://maps.google.com/?q=${encodedAddress}`).catch(() => {
        Alert.alert('Error', 'Could not open maps app');
      });
    }
  };

  const formatRating = (rating: string | number | undefined) => {
    if (!rating) return 'N/A';
    const numRating = typeof rating === 'string' ? parseFloat(rating) : rating;
    return numRating.toFixed(1);
  };

  const getVenueTypeIcon = (venueType?: string) => {
    switch (venueType) {
      case 'tiki_bar':
        return '🌺';
      case 'craft_cocktail_bar':
        return '🍸';
      case 'lounge':
        return '🥂';
      case 'bar':
        return '🍺';
      default:
        return '🏪';
    }
  };

  const getVenueTypeLabel = (venueType?: string) => {
    switch (venueType) {
      case 'tiki_bar':
        return 'Tiki Bar';
      case 'craft_cocktail_bar':
        return 'Craft Cocktail Bar';
      case 'lounge':
        return 'Lounge';
      case 'bar':
        return 'Bar';
      default:
        return 'Venue';
    }
  };

  if (loading) {
    return (
      <ThemedContainer style={styles.container}>
        <View style={styles.loadingContainer}>
          <Ionicons name="refresh" size={48} color={theme.colors.primary} />
          <ThemedText style={styles.loadingText}>Loading venue details...</ThemedText>
        </View>
      </ThemedContainer>
    );
  }

  if (error || !venue) {
    return (
      <ThemedContainer style={styles.container}>
        <View style={styles.errorContainer}>
          <Ionicons name="alert-circle" size={48} color={theme.colors.error} />
          <ThemedText style={styles.errorText}>{error || 'Venue not found'}</ThemedText>
          <Button mode="contained" onPress={() => navigation.goBack()}>
            Go Back
          </Button>
        </View>
      </ThemedContainer>
    );
  }

  return (
    <ThemedContainer style={styles.container}>
      <ScrollView showsVerticalScrollIndicator={false}>
        {/* Header Section */}
        <View style={styles.header}>
                     <View style={styles.headerContent}>
             <ThemedText style={styles.venueIcon}>{getVenueTypeIcon(venue.venue_type)}</ThemedText>
                         <ThemedText size="title" style={styles.venueName}>
               {venue.name}
             </ThemedText>
            <Chip 
              mode="outlined" 
              style={styles.venueTypeChip}
              textStyle={{ color: theme.colors.primary }}
            >
              {getVenueTypeLabel(venue.venue_type)}
            </Chip>
          </View>
        </View>

        {/* Rating Section */}
        {venue.rating_avg && (
          <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
            <Card.Content>
                             <View style={styles.ratingContainer}>
                 <Ionicons name="star" size={24} color="#FFD700" />
                 <ThemedText style={styles.ratingText}>
                   (MOCK) {formatRating(venue.rating_avg)}
                 </ThemedText>
                 {venue.rating_count && (
                   <ThemedText variant="secondary" style={styles.ratingCount}>
                     (MOCK) ({venue.rating_count} reviews)
                   </ThemedText>
                 )}
               </View>
            </Card.Content>
          </Card>
        )}

        {/* Location Section */}
        <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Card.Content>
                         <ThemedText size="large" style={styles.sectionTitle}>
               📍 Location
             </ThemedText>
                         <View style={styles.locationInfo}>
               {venue.address && (
                 <ThemedText style={styles.addressText}>
                   {venue.address}
                 </ThemedText>
               )}
               <ThemedText variant="secondary" style={styles.locationText}>
                 {[venue.city, venue.region, venue.country].filter(Boolean).join(', ')}
               </ThemedText>
             </View>
          </Card.Content>
        </Card>

        {/* Description Section */}
        {venue.description && (
          <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
            <Card.Content>
                           <ThemedText size="large" style={styles.sectionTitle}>
               📝 About
             </ThemedText>
              <ThemedText style={styles.descriptionText}>
                {venue.description}
              </ThemedText>
            </Card.Content>
          </Card>
        )}

        {/* Actions Section */}
        <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Card.Content>
                         <ThemedText size="large" style={styles.sectionTitle}>
               🚀 Quick Actions
             </ThemedText>
            <View style={styles.actionsContainer}>
              {venue.google_phone && (
                <Button
                  mode="outlined"
                  onPress={() => handleCall(venue.google_phone!)}
                  style={styles.actionButton}
                  icon="phone"
                >
                  Call
                </Button>
              )}
              {venue.google_website && (
                <Button
                  mode="outlined"
                  onPress={() => handleWebsite(venue.google_website!)}
                  style={styles.actionButton}
                  icon="web"
                >
                  Website
                </Button>
              )}
              <Button
                mode="outlined"
                onPress={() => handleDirections(venue.address, venue.city, venue.region)}
                style={styles.actionButton}
                icon="map"
              >
                Directions
              </Button>
            </View>
          </Card.Content>
        </Card>

        {/* Additional Info Section */}
        <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Card.Content>
                         <ThemedText size="large" style={styles.sectionTitle}>
               ℹ️ Additional Info
             </ThemedText>
                         <View style={styles.infoContainer}>
               {venue.rating_source && (
                 <View style={styles.infoRow}>
                   <ThemedText variant="secondary" style={styles.infoLabel}>
                     Rating Source:
                   </ThemedText>
                   <ThemedText style={styles.infoValue}>
                     (MOCK) {venue.rating_source}
                   </ThemedText>
                 </View>
               )}
               {venue.last_rating_update && (
                 <View style={styles.infoRow}>
                   <ThemedText variant="secondary" style={styles.infoLabel}>
                     Last Updated:
                   </ThemedText>
                   <ThemedText style={styles.infoValue}>
                     (MOCK) {new Date(venue.last_rating_update).toLocaleDateString()}
                   </ThemedText>
                 </View>
               )}
             </View>
          </Card.Content>
        </Card>

        {/* Comments Section */}
        <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Card.Content>
                         <ThemedText size="large" style={styles.sectionTitle}>
               💬 Comments
             </ThemedText>
            <View style={styles.commentsContainer}>
                             <View style={styles.commentItem}>
                 <View style={styles.commentHeader}>
                   <ThemedText style={{ ...styles.commentAuthor, color: theme.colors.primary }}>
                     TikiEnthusiast42
                   </ThemedText>
                   <ThemedText variant="secondary" style={styles.commentDate}>
                     (MOCK) 2 days ago
                   </ThemedText>
                 </View>
                 <ThemedText style={styles.commentText}>
                   (MOCK) Amazing atmosphere! The themed cocktails are incredible. Definitely worth the wait to get in.
                 </ThemedText>
               </View>
               <View style={styles.commentItem}>
                 <View style={styles.commentHeader}>
                   <ThemedText style={{ ...styles.commentAuthor, color: theme.colors.primary }}>
                     RumLover2024
                   </ThemedText>
                   <ThemedText variant="secondary" style={styles.commentDate}>
                     (MOCK) 1 week ago
                   </ThemedText>
                 </View>
                 <ThemedText style={styles.commentText}>
                   (MOCK) Great selection of rums and the staff really knows their stuff. Can't wait to go back!
                 </ThemedText>
               </View>
               <View style={styles.commentItem}>
                 <View style={styles.commentHeader}>
                   <ThemedText style={{ ...styles.commentAuthor, color: theme.colors.primary }}>
                     TropicalDreamer
                   </ThemedText>
                   <ThemedText variant="secondary" style={styles.commentDate}>
                     (MOCK) 2 weeks ago
                   </ThemedText>
                 </View>
                 <ThemedText style={styles.commentText}>
                   (MOCK) Perfect spot for a date night. The decor is so immersive and the drinks are top-notch.
                 </ThemedText>
               </View>
            </View>
          </Card.Content>
        </Card>

        {/* Navigation Footer */}
        <View style={styles.footer}>
          <Button
            mode="contained"
            onPress={() => navigation.goBack()}
            style={styles.backButton}
          >
            <Ionicons name="arrow-back" size={16} />
            Back to Venues
          </Button>
        </View>
      </ScrollView>
    </ThemedContainer>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorText: {
    marginTop: 16,
    marginBottom: 20,
    fontSize: 16,
    textAlign: 'center',
  },
  header: {
    padding: 20,
    paddingBottom: 10,
  },
  headerContent: {
    alignItems: 'center',
  },
  venueIcon: {
    fontSize: 48,
    marginBottom: 10,
  },
  venueName: {
    fontSize: 24,
    textAlign: 'center',
    marginBottom: 10,
  },
  venueTypeChip: {
    marginBottom: 10,
  },
  card: {
    margin: 10,
    marginTop: 5,
    marginBottom: 5,
  },
  ratingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  ratingText: {
    fontSize: 20,
    fontWeight: 'bold',
    marginLeft: 8,
  },
  ratingCount: {
    fontSize: 14,
    marginLeft: 8,
  },
  sectionTitle: {
    fontSize: 18,
    marginBottom: 10,
  },
  locationInfo: {
    alignItems: 'center',
  },
  addressText: {
    fontSize: 16,
    fontWeight: '500',
    textAlign: 'center',
    marginBottom: 5,
  },
  locationText: {
    fontSize: 14,
    textAlign: 'center',
  },
  descriptionText: {
    fontSize: 14,
    lineHeight: 20,
  },
  showMoreButton: {
    marginTop: 10,
    alignItems: 'center',
  },
  showMoreText: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  actionsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
    gap: 10,
  },
  actionButton: {
    marginVertical: 5,
    minWidth: 100,
  },
  infoContainer: {
    gap: 10,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  infoLabel: {
    fontSize: 14,
    fontWeight: '500',
  },
  infoValue: {
    fontSize: 14,
  },
  commentsContainer: {
    gap: 15,
  },
  commentItem: {
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
  },
  commentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 5,
  },
  commentAuthor: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  commentDate: {
    fontSize: 12,
  },
  commentText: {
    fontSize: 14,
    lineHeight: 20,
  },
  footer: {
    padding: 20,
    paddingTop: 10,
  },
  backButton: {
    marginTop: 10,
  },
});

export default VenueDetailScreen;
